/* The canvas-ops engine: P0–P5 pipeline, clone-apply, inverse capture (D12), revert
   composition, and the remove sweep (shared with the D11 manual-delete guard).

   PURE MODULE — no Vue, no store. `applyProposal` never mutates its input canvas; it
   returns a fresh canvas for the store to swap wholesale (Vue 2 reactivity contract).

   Semantics pinned by the proposal (§4) and the schema's x-validation-pipeline:
   - net-state: ops apply in order to a working clone; invariants and sweeps evaluate the
     result, so cascades ride inside one proposal (D9).
   - delta invariants: only violations the proposal INTRODUCES refuse — real canvases carry
     ancient violations and the guard must still operate on them.
   - atomicity: any refusal anywhere → the caller gets ok:false and the original canvas is
     untouched (the working clone is discarded).
   - stale addresses refuse, never guess; identity keys are immutable; numeric ids are
     string-coerced everywhere (old canvases carry numbers). */

import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";
import SCHEMA from "./canvasguard-schema.json";

import {
  UNIT_CATALOG,
  tierOfKey,
  tierOfSection,
  definitionFor,
  propertySchema,
  FIELD_TYPES,
  GRAPH_KEYS,
} from "./schemaTable";
import { formulaReferencesField, formulaReferencesSubField, extractFormulaRefs, hasChips } from "./formulaRefs";
import nativeFormula from "../nativeFormula";
import { findUnknownFieldFunctionNames } from "../../components/design/formula/formulaDiagnostics";

const sameId = (a, b) => a != null && b != null && String(a) === String(b);
const getPath = (node, dotted) =>
  dotted.split(".").reduce((n, k) => (n == null ? n : n[k]), node);

const FORMULA_KEYS = ["formula", "visibleFormula", "readOnlyFormula", "requiredFormula"];
const REFERENCE_REQUIRED_TYPES = [7, 8]; // Locus/Locuses need connectionId; User/Users ship without one in real canvases
const REFERENCE_TYPES = [7, 8, 18, 19];

let recordCounter = 0;

/* ---- error plumbing ------------------------------------------------------------------ */

const err = (stage, code, message, extra) =>
  Object.assign({ severity: "error", stage, code, message }, extra || {});
const warn = (stage, code, message, extra) =>
  Object.assign({ severity: "warn", stage, code, message }, extra || {});

/* ---- P0: grammar --------------------------------------------------------------------- */

const grammarCheck = (op) => {
  const errors = [];
  if (!op || typeof op !== "object") return [err("P0", "grammar", "op is not an object")];
  if (["upsert", "remove", "move", "set"].indexOf(op.op) < 0)
    errors.push(err("P0", "grammar", `unknown verb '${op.op}'`));
  if (!Array.isArray(op.address) ||
      op.address.some((h) => !Array.isArray(h) || h.length !== 2 || typeof h[0] !== "string"))
    errors.push(err("P0", "grammar", "address must be an array of [collection, id] hops"));
  if (op.op === "set") {
    if (typeof op.key !== "string" || !op.key)
      errors.push(err("P0", "grammar",
        "set needs a 'key' (a string). The key rides as its own parameter — never as an address hop: to set a field's formula, address the field and pass key: \"formula\"."));
  }
  if (op.op === "upsert") {
    if (op.item == null || typeof op.item !== "object" || Array.isArray(op.item))
      errors.push(err("P0", "grammar",
        "upsert requires an object 'item' (whole-list emission is refused — address the member)"));
  }
  if (op.op === "move") {
    const anchors = (op.before != null ? 1 : 0) + (op.after != null ? 1 : 0);
    if (anchors !== 1)
      errors.push(err("P0", "grammar", "move requires exactly one of 'before'/'after' (a sibling id)"));
  }
  if (op.op !== "upsert" && op.address && op.address.length === 0 && op.op !== "set")
    errors.push(err("P0", "grammar", `'${op.op}' cannot target the canvas root`));
  return errors;
};

/* ---- P1: addressing ------------------------------------------------------------------ */
/* Resolve an address chain on `canvas`. Returns:
   { unitName, parentNode, collPath, collection, index, node, hops } or { errors }.
   For upsert with a missing FINAL member: node null, index -1 (create mode).
   `scaffold` (upsert only) auto-creates missing intermediate containers of the final
   collection ("missing design/rows scaffold auto-created — pure structure"). */
const resolveAddress = (canvas, address, opts) => {
  const forUpsert = !!(opts && opts.forUpsert);
  let unitName = "root";
  let node = canvas;
  let out = null;

  if (!address.length) return { errors: [err("P1", "address", "empty address")] };

  for (let i = 0; i < address.length; i++) {
    const [collName, id] = address[i];
    const unit = UNIT_CATALOG[unitName];
    const children = (unit && unit.children) || {};
    const childUnit = children[collName];
    if (!childUnit)
      return { errors: [err("P1", "address",
        `'${collName}' is not an addressable collection of ${unitName} (unit catalog §4)`)] };

    let collection = getPath(node, collName);
    const isFinal = i === address.length - 1;
    if (!Array.isArray(collection)) {
      if (forUpsert && isFinal) {
        // scaffold the container chain on the working clone (pure structure)
        const parts = collName.split(".");
        let holder = node;
        for (let p = 0; p < parts.length - 1; p++) {
          if (holder[parts[p]] == null || typeof holder[parts[p]] !== "object") holder[parts[p]] = {};
          holder = holder[parts[p]];
        }
        if (!Array.isArray(holder[parts[parts.length - 1]])) holder[parts[parts.length - 1]] = [];
        collection = getPath(node, collName);
      } else {
        return { errors: [err("P1", "address",
          `collection '${collName}' does not exist at ${describeHops(address, i)}`)] };
      }
    }

    const idKey = UNIT_CATALOG[childUnit].idKey;
    const index = collection.findIndex((m) => m && sameId(m[idKey], id));
    const member = index >= 0 ? collection[index] : null;

    if (member == null && !(forUpsert && isFinal)) {
      return { errors: [err("P1", "address",
        `no ${childUnit} with ${idKey} '${id}' at ${describeHops(address, i)} — stale addresses refuse, never guess`)] };
    }

    out = { unitName: childUnit, parentNode: node, collPath: collName, collection, index, node: member, id };
    unitName = childUnit;
    node = member;
  }
  return out;
};

const describeHops = (address, upto) =>
  address.slice(0, upto + 1).map((h) => h[0] + "[" + h[1] + "]").join(".");

/* ---- P2/P3: policy + shape (payload walks) ------------------------------------------- */

const tierAt = (defNode, key) => {
  if (GRAPH_KEYS.indexOf(key) >= 0) return "graph";
  const prop = defNode && defNode.properties ? resolveProp(defNode.properties[key]) : null;
  if (!prop) return "unknown";
  if (prop["x-ai-write"]) return prop["x-ai-write"];
  return "open";
};

const resolveProp = (node) => {
  if (node && node.$ref) return SCHEMA.definitions[node.$ref.replace("#/definitions/", "")] || null;
  return node || null;
};

const arrayItemDef = (propNode) => resolveProp(propNode && propNode.items);
const itemIdKeyOf = (itemDef) => {
  if (!itemDef || !itemDef.properties) return null;
  if (itemDef === SCHEMA.definitions.DesignRow) return "index";
  return itemDef.properties.id ? "id" : null;
};

/* Deep policy walk for an upsert payload. Builds the MERGED node (payload + code/graph
   carry-over from `oldNode` at id-aligned paths), collecting refusals/warnings and every
   formula-tier write for the P4 formula check.
   mode: "create" (oldNode null) | "replace". */
const policyWalk = (defNode, oldNode, payload, mode, path, out) => {
  const merged = Array.isArray(payload) ? [] : {};

  for (const key of Object.keys(payload)) {
    const value = payload[key];
    const here = path ? path + "." + key : key;
    const tier = tierAt(defNode, key);
    const oldValue = oldNode != null ? oldNode[key] : undefined;

    if (tier === "code" || tier === "graph") {
      const label = tier === "graph" ? "automation graph" : "code";
      if (mode === "create" || oldValue === undefined) {
        out.errors.push(err("P2", "code-omit",
          `'${here}' is ${label} (${tier} tier) — omit it; scripts are never authored through ops (D5/D7)`));
      } else if (!isEqual(oldValue, value)) {
        out.errors.push(err("P2", "code-change",
          `'${here}' would change a ${label} value — code keys are carried over verbatim, never changed (D7)`));
      } else {
        merged[key] = cloneDeep(oldValue);
      }
      continue;
    }
    if (tier === "derived") {
      out.errors.push(err("P2", "tier",
        `'${here}' is derived/server-stamped — never written through ops`));
      continue;
    }
    if (tier === "read-only") {
      out.errors.push(err("P2", "tier", `'${here}' is read-only for ops (D8 for authorizations)`));
      continue;
    }

    const prop = defNode && defNode.properties ? resolveProp(defNode.properties[key]) : null;

    if (tier === "formula") {
      if (value != null && value !== "") out.formulaWrites.push({ path: here, value });
      merged[key] = cloneDeep(value);
      continue;
    }

    // open/unknown: recurse where the schema knows structure, to catch nested code keys
    if (prop && prop.type === "object" && value != null && typeof value === "object" && !Array.isArray(value)) {
      merged[key] = policyWalk(prop, oldValue != null ? oldValue : null, value, mode, here, out);
    } else if (prop && Array.isArray(value) && arrayItemDef(prop)) {
      const itemDef = arrayItemDef(prop);
      const idKey = itemIdKeyOf(itemDef);
      const oldArr = Array.isArray(oldValue) ? oldValue : [];
      merged[key] = value.map((entry, n) => {
        if (entry == null || typeof entry !== "object")
          return cloneDeep(entry);
        const oldMatch = idKey
          ? oldArr.find((o) => o && sameId(o[idKey], entry[idKey]))
          : undefined;
        return policyWalk(itemDef, oldMatch != null ? oldMatch : null, entry,
          oldMatch != null ? "replace" : mode, here + "[" + n + "]", out);
      });
      if (idKey) {
        // members the payload drops that carry code: allowed, but say so (P2 remove rule)
        for (const o of oldArr) {
          if (o && !value.some((entry) => entry && sameId(entry[idKey], o[idKey]))) {
            const n = countCodeValues(itemDef, o);
            if (n > 0)
              out.warnings.push(warn("P2", "drops-code",
                `replacing '${here}' drops a member (${idKey} '${o[idKey]}') that carries ${n} script(s)`));
          }
        }
      }
    } else {
      merged[key] = cloneDeep(value);
    }
  }

  // carry over code/graph keys the payload omitted (replace only)
  if (oldNode != null) {
    for (const key of Object.keys(oldNode)) {
      if (merged[key] !== undefined || payload[key] !== undefined) continue;
      const tier = tierAt(defNode, key);
      if (tier === "code" || tier === "graph") merged[key] = cloneDeep(oldNode[key]);
    }
  }
  return merged;
};

/* Count non-empty code/graph values inside `value` per its definition (for drop warnings
   and remove-op script notices). */
const countCodeValues = (defNode, value) => {
  if (value == null || typeof value !== "object") return 0;
  let n = 0;
  const props = (defNode && defNode.properties) || {};
  for (const key of Object.keys(value)) {
    const tier = tierAt(defNode, key);
    const v = value[key];
    if ((tier === "code" && v != null && v !== "") ||
        (tier === "graph" && v != null && typeof v === "object" && Object.keys(v).length)) {
      n++;
      continue;
    }
    const prop = props[key] ? resolveProp(props[key]) : null;
    if (prop && prop.type === "object" && v && typeof v === "object" && !Array.isArray(v)) {
      n += countCodeValues(prop, v);
    } else if (prop && Array.isArray(v) && arrayItemDef(prop)) {
      for (const entry of v) n += countCodeValues(arrayItemDef(prop), entry);
    }
  }
  return n;
};

/* P3: schema-shape validation of a payload against the unit definition.
   Required keys, declared types, enums; unknown keys warn (the canvas is open — but
   AI-emitted unknowns are usually hallucinations). */
const typeOk = (declared, value) => {
  const types = Array.isArray(declared) ? declared : [declared];
  return types.some((t) => {
    switch (t) {
      case "string": return typeof value === "string";
      case "boolean": return typeof value === "boolean";
      case "number": return typeof value === "number" && isFinite(value);
      case "integer": return typeof value === "number" && Number.isInteger(value);
      case "array": return Array.isArray(value);
      case "object": return value != null && typeof value === "object" && !Array.isArray(value);
      case "null": return value === null;
      default: return true;
    }
  });
};

const shapeCheck = (defNode, value, path, out, opts) => {
  if (!defNode || value == null || typeof value !== "object") return;
  const props = defNode.properties || {};
  const required = defNode.required || [];
  for (const key of required) {
    // on create the address supplies identity; the engine backfills id/fieldName before this
    if (value[key] == null || value[key] === "")
      out.errors.push(err("P3", "required", `'${path ? path + "." : ""}${key}' is required`));
  }
  for (const key of Object.keys(value)) {
    const here = path ? path + "." + key : key;
    const propRaw = props[key];
    if (!propRaw) {
      if (!opts || !opts.silentUnknown)
        out.warnings.push(warn("P3", "unknown-key",
          `'${here}' is not a key the canvas schema describes — kept, but verify it is intended`));
      continue;
    }
    const prop = resolveProp(propRaw);
    const v = value[key];
    if (v == null) continue;
    if (prop.enum && prop.enum.indexOf(v) < 0) {
      out.errors.push(err("P3", "enum", `'${here}' must be one of ${prop.enum.join(", ")}`));
      continue;
    }
    if (prop.type && !typeOk(prop.type, v)) {
      out.errors.push(err("P3", "type",
        `'${here}' should be ${Array.isArray(prop.type) ? prop.type.join("|") : prop.type}`));
      continue;
    }
    if (prop.type === "object" && typeof v === "object" && !Array.isArray(v)) {
      shapeCheck(prop, v, here, out, opts);
    } else if (Array.isArray(v) && arrayItemDef(prop)) {
      for (let n = 0; n < v.length; n++) {
        if (v[n] != null && typeof v[n] === "object")
          shapeCheck(arrayItemDef(prop), v[n], here + "[" + n + "]", out, opts);
      }
    }
  }
};

/* ---- P4: invariants (delta-checked) --------------------------------------------------- */
/* Collect violations as stable keys so post − pre isolates what the proposal introduced. */

const lower = (s) => (typeof s === "string" ? s.trim().toLowerCase() : "");

export const collectViolations = (canvas) => {
  const v = new Map(); // key -> message
  const add = (key, message) => { if (!v.has(key)) v.set(key, message); };

  const info = canvas.information || [];
  const seenNames = new Map();
  for (const f of info) {
    if (!f) continue;
    const dn = lower(f.displayName);
    if (!dn) add(`info-name-empty:${f.id}`, `field '${f.id}' has an empty display name`);
    else if (seenNames.has(dn))
      add(`info-name-dup:${dn}`, `duplicate field display name '${f.displayName}'`);
    else seenNames.set(dn, f.id);

    if (f.type == null || FIELD_TYPES.indexOf(f.type) < 0)
      add(`info-type:${f.id}`, `field '${f.displayName}' has an unknown type (${f.type})`);
    if ((f.type === 14 || f.type === 15) && !(f.optionList && f.optionList.length))
      add(`info-options:${f.id}`, `Select/Tag field '${f.displayName}' needs at least one option`);
    if ((f.type === 16 || f.type === 17) && !(f.fields && f.fields.length))
      add(`info-subfields:${f.id}`, `Table/List field '${f.displayName}' needs at least one sub-field`);
    if (REFERENCE_REQUIRED_TYPES.indexOf(f.type) >= 0 && (f.connectionId == null || f.connectionId === ""))
      add(`info-connection:${f.id}`, `reference field '${f.displayName}' needs a connectionId`);
    if (REFERENCE_TYPES.indexOf(f.type) < 0 && f.connectionId != null && f.connectionId !== "")
      add(`info-connection-extra:${f.id}`,
        `field '${f.displayName}' is not a reference type but carries a connectionId`);
    if (f.type === 25)
      add(`info-daterange:${f.id}`,
        `DateRange fields cannot be created through ops (validator parity)`);

    const subNames = new Map();
    for (const s of f.fields || []) {
      if (!s) continue;
      const sn = lower(s.displayName);
      if (!sn) add(`sub-name-empty:${f.id}:${s.id}`, `sub-field '${s.id}' of '${f.displayName}' has an empty name`);
      else if (subNames.has(sn))
        add(`sub-name-dup:${f.id}:${sn}`, `duplicate sub-field name '${s.displayName}' on '${f.displayName}'`);
      else subNames.set(sn, s.id);
      if (REFERENCE_TYPES.indexOf(s.type) >= 0)
        add(`sub-reftype:${f.id}:${s.id}`,
          `sub-field '${s.displayName}' of '${f.displayName}': reference types are not allowed on sub-fields (validator parity)`);
      if ((s.type === 14 || s.type === 15) && !(s.optionList && s.optionList.length))
        add(`sub-options:${f.id}:${s.id}`, `Select/Tag sub-field '${s.displayName}' needs at least one option`);
    }

    collectOptionViolations(f, `opt:${f.id}`, add);
    for (const s of f.fields || []) collectOptionViolations(s, `opt:${f.id}:${s && s.id}`, add);
  }

  const states = canvas.states || [];
  if (states.length) {
    const initials = states.filter((s) => s && s.initial === true).length;
    if (initials !== 1)
      add(`states-initial:${initials}`, `exactly one state must be initial (found ${initials})`);
    const stateNames = new Map();
    for (const s of states) {
      if (!s) continue;
      const sn = lower(s.name);
      if (!sn) add(`state-name-empty:${s.id}`, `state '${s.id}' has an empty name`);
      else if (stateNames.has(sn)) add(`state-name-dup:${sn}`, `duplicate state name '${s.name}'`);
      else stateNames.set(sn, s.id);
    }
  }

  if (typeof canvas.ai_instruction === "string" && canvas.ai_instruction.length > 2000)
    add("root-ai-instruction-len", "ai_instruction exceeds 2000 characters (read-time cap)");

  if (canvas.archive && Array.isArray(canvas.archive.states)) {
    for (const sid of canvas.archive.states) {
      if (!states.some((s) => s && sameId(s.id, sid)))
        add(`archive-state:${sid}`, `archive.states references unknown state '${sid}'`);
    }
  }

  /* forward references — flows, triggers, menu scoping, documents (net state; delta rule
     means only refs a proposal breaks or writes broken refuse) */
  const stateIds = new Set(states.filter(Boolean).map((s) => String(s.id)));
  const activityIds = new Set((canvas.activities || []).filter(Boolean).map((a) => String(a.id)));
  const menuIds = new Set((canvas.menus || []).filter(Boolean).map((m) => String(m.id)));
  const documentIds = new Set((canvas.documents || []).filter(Boolean).map((d) => String(d.id)));
  const infoIds = new Set(info.filter(Boolean).map((f) => String(f.id)));

  for (const fl of canvas.flows || []) {
    if (!fl) continue;
    if (fl.toStateId == null || fl.toStateId === "")
      add(`flow-tostate:${fl.id}`, `flow '${fl.id}' has no target state`);
    else if (!stateIds.has(String(fl.toStateId)))
      add(`flow-ref:${fl.id}:${fl.toStateId}`, `flow '${fl.id}' points at unknown state '${fl.toStateId}'`);
    if (fl.fromStateId != null && fl.fromStateId !== "" && !stateIds.has(String(fl.fromStateId)))
      add(`flow-ref:${fl.id}:${fl.fromStateId}`, `flow '${fl.id}' starts from unknown state '${fl.fromStateId}'`);
    if (fl.activityId != null && fl.activityId !== "" && !activityIds.has(String(fl.activityId)))
      add(`flow-activity:${fl.id}`, `flow '${fl.id}' is driven by unknown activity '${fl.activityId}'`);
    for (const cs of fl.conditionalStates || []) {
      if (cs && cs.toStateId && !stateIds.has(String(cs.toStateId)))
        add(`flow-cond:${fl.id}:${cs.id}`, `conditional route '${cs.id}' targets unknown state '${cs.toStateId}'`);
    }
  }

  const activityNames = new Map();
  for (const a of canvas.activities || []) {
    if (!a) continue;
    const an = lower(a.name);
    if (!an) add(`act-name-empty:${a.id}`, `activity '${a.id}' has an empty name`);
    else if (activityNames.has(an)) add(`act-name-dup:${an}`, `duplicate activity name '${a.name}'`);
    else activityNames.set(an, a.id);
    if (a.actor != null && ["human", "ai", "hybrid"].indexOf(a.actor) < 0)
      add(`act-actor:${a.id}`, `activity '${a.name}': actor must be human, ai or hybrid`);
    if (a.confidence_threshold != null &&
        (typeof a.confidence_threshold !== "number" || a.confidence_threshold < 0 || a.confidence_threshold > 1))
      add(`act-confidence:${a.id}`, `activity '${a.name}': confidence_threshold must be within [0, 1]`);
    if (a.documentId != null && a.documentId !== "" && !documentIds.has(String(a.documentId)))
      add(`act-document:${a.id}`, `activity '${a.name}' is bound to unknown document '${a.documentId}'`);
  }

  for (const [section, list] of [
    ["activities", canvas.activities], ["standardActivities", canvas.standardActivities], ["actions", canvas.actions],
  ]) {
    for (const entry of list || []) {
      if (!entry) continue;
      for (const mid of entry.menus || []) {
        if (!menuIds.has(String(mid)))
          add(`menus-scope:${section}:${entry.id}:${mid}`,
            `'${entry.name || entry.id}' is scoped to unknown listing '${mid}'`);
      }
    }
  }

  for (const m of canvas.menus || []) {
    if (!m) continue;
    if (!lower(m.name)) add(`menu-name-empty:${m.id}`, `listing '${m.id}' has an empty name`);
    if (m.logicId != null && m.logicId !== "" &&
        !(canvas.logicServices || []).some((l) => l && sameId(l.id, m.logicId)))
      add(`menu-logic:${m.id}`, `listing '${m.name}' is backed by unknown logic service '${m.logicId}'`);
  }

  for (const t of canvas.triggers || []) {
    if (!t) continue;
    if (t.stateId != null && t.stateId !== "" && !stateIds.has(String(t.stateId)))
      add(`trigger-state:${t.id}`, `automation '${t.name || t.id}' fires on unknown state '${t.stateId}'`);
    if (t.activityId != null && t.activityId !== "" && !activityIds.has(String(t.activityId)))
      add(`trigger-activity:${t.id}`, `automation '${t.name || t.id}' fires on unknown activity '${t.activityId}'`);
    if (t.repeat === true && (!t.recurringType || t.every == null))
      add(`trigger-schedule:${t.id}`, `automation '${t.name || t.id}': repeat needs recurringType and every`);
    // D19: a present block graph must carry a start block (the runner's entry point)
    for (const phase of ["automation", "automationBefore"]) {
      const graph = t[phase];
      if (graph && typeof graph === "object" && graph.blocks && typeof graph.blocks === "object" &&
          !Array.isArray(graph.blocks) && Object.keys(graph.blocks).length &&
          !Object.keys(graph.blocks).some((k) =>
            graph.blocks[k] && graph.blocks[k].name === "start"))
        add(`trigger-graph:${t.id}:${phase}`, `automation '${t.name || t.id}': its ${phase} graph has no start block`);
    }
  }

  const SENIORITIES = ["junior", "senior", "expert", "consultant"];
  const checkAiActions = (design, hostLabel) => {
    if (!design || !Array.isArray(design.aiActions)) return;
    for (const act of design.aiActions) {
      if (!act) continue;
      if (act.seniority != null && SENIORITIES.indexOf(act.seniority) < 0)
        add(`aiaction-seniority:${act.id}`, `GNey action '${act.label || act.id}' on ${hostLabel}: unknown seniority '${act.seniority}'`);
      for (const fid of act.fillScope || []) {
        if (!infoIds.has(String(fid)))
          add(`aiaction-fillscope:${act.id}:${fid}`,
            `GNey action '${act.label || act.id}' on ${hostLabel}: fillScope names unknown field '${fid}'`);
      }
    }
  };
  for (const a of canvas.activities || []) if (a) checkAiActions(a.design, `activity '${a.name || a.id}'`);
  for (const a of canvas.standardActivities || []) if (a) checkAiActions(a.design, `form '${a.name || a.id}'`);
  for (const d of canvas.documents || []) if (d) checkAiActions(d.userInput, `document '${d.name || d.id}'`);
  walkDesigns(canvas, (item, hostLabel) => {
    if (item.aiFill && item.aiFill.seniority != null && SENIORITIES.indexOf(item.aiFill.seniority) < 0)
      add(`aifill-seniority:${item.id}`, `GNey fill on ${hostLabel}: unknown seniority '${item.aiFill.seniority}'`);
  });

  return v;
};

const collectOptionViolations = (holder, prefix, add) => {
  if (!holder || !Array.isArray(holder.optionList)) return;
  const names = new Map();
  for (const o of holder.optionList) {
    if (!o) continue;
    const on = lower(o.name);
    if (!on) add(`${prefix}:empty:${o.id}`, `option '${o.id}' has an empty name`);
    else if (names.has(on)) add(`${prefix}:dup:${on}`, `duplicate option name '${o.name}'`);
    else names.set(on, o.id);
  }
};

/* Formula-lint check on written formula values. Refuse = unknown UPPERCASE function names
   (parity with the save-time bounce), only when the caller supplies the function registry.
   A failed native parse is a WARN, not a refusal — legacy Python-lane formulas ({{chips}},
   Python syntax) are legal canvas content the native tokenizer cannot read. */
const checkFormulaWrites = (formulaWrites, context, out) => {
  const known = context && context.formulaKnownNames;
  for (const w of formulaWrites) {
    if (typeof w.value !== "string" || !w.value.trim()) continue;
    if (known && known.size) {
      const unknown = findUnknownFieldFunctionNames(w.value, known);
      if (unknown.length) {
        out.errors.push(err("P4", "formula-unknown-fn",
          `'${w.path}': unknown function ${unknown[0]}(…) — the save would bounce`));
        continue;
      }
    }
    if (!hasChips(w.value)) {
      try {
        nativeFormula.compile(w.value);
      } catch (e) {
        out.warnings.push(warn("P4", "formula-parse",
          `'${w.path}' does not parse in the native formula dialect (${e.message}) — verify it is a legacy-lane expression`));
      }
    }
  }
};

/* ---- P5: reference sweeps ------------------------------------------------------------- */

/* Walk every form design in the canvas: activities, standardActivities, documents'
   userInput — recursing into nested item designs and tab designs.
   cb(item, hostLabel, address) — address is the full op-address chain to the item. */
export const walkDesigns = (canvas, cb) => {
  const walkDesign = (design, hostLabel, base, rowsSegment) => {
    if (!design || !Array.isArray(design.rows)) return;
    for (const row of design.rows) {
      if (!row || !Array.isArray(row.items)) continue;
      for (const item of row.items) {
        if (!item) continue;
        const address = base.concat([[rowsSegment, row.index], ["items", item.id]]);
        cb(item, hostLabel, address);
        if (item.design)
          walkDesign(item.design, hostLabel + " › group", address, "design.rows");
        for (const tab of item.tabs || []) {
          if (tab && tab.design)
            walkDesign(tab.design, hostLabel + " › tab '" + (tab.header || tab.id) + "'",
              address.concat([["tabs", tab.id]]), "design.rows");
        }
      }
    }
  };
  for (const a of canvas.activities || [])
    if (a) walkDesign(a.design, `activity '${a.name || a.id}'`, [["activities", a.id]], "design.rows");
  for (const a of canvas.standardActivities || [])
    if (a) walkDesign(a.design, `form '${a.name || a.id}'`, [["standardActivities", a.id]], "design.rows");
  for (const d of canvas.documents || [])
    if (d) walkDesign(d.userInput, `document '${d.name || d.id}'`, [["documents", d.id]], "userInput.rows");
};

/* cb(item, address) over the non-group conditions of one filter group.
   `base` is the address of the group's items collection owner (menu or group item). */
const walkFilterItems = (group, cb, base, segment) => {
  if (!group || !Array.isArray(group.items)) return;
  for (const it of group.items) {
    if (!it) continue;
    const address = base ? base.concat([[segment, it.id]]) : null;
    if (it.type === "or" || Array.isArray(it.items))
      walkFilterItems(it, cb, address, "items");
    if (it.type !== "or") cb(it, address);
  }
};

const row = (kind, code, label) => ({ kind, code, label });

/* Non-empty code/graph values anywhere in the canvas make name-based references
   unscannable — one honest warn row. */
const moduleHasScripts = (canvas) => {
  let found = false;
  const scan = (node) => {
    if (found || node == null) return;
    if (Array.isArray(node)) { node.forEach(scan); return; }
    if (typeof node !== "object") return;
    for (const key of Object.keys(node)) {
      const v = node[key];
      if ((/[sS]cript$/.test(key) || key === "condition") &&
          typeof v === "string" && v.trim()) { found = true; return; }
      if (key === "scripts" && v && typeof v === "object" &&
          Object.keys(v).some((k) => typeof v[k] === "string" && v[k].trim())) { found = true; return; }
      scan(v);
    }
  };
  scan(canvas);
  return found;
};

const sweepInformationField = (canvas, field) => {
  const rows = [];
  walkDesigns(canvas, (item, host) => {
    if (!item.custom && sameId(item.id, field.id))
      rows.push(row("blocking", "design-item", `form element on ${host}`));
    for (const fk of FORMULA_KEYS) {
      if (typeof item[fk] === "string" && item[fk] &&
          formulaReferencesField(item[fk], field))
        rows.push(row("blocking", "formula",
          `${fk} on a form element of ${host} references '${field.displayName}'`));
    }
    for (const cfg of item.fields || []) {
      for (const fk of FORMULA_KEYS) {
        if (cfg && typeof cfg[fk] === "string" && cfg[fk] &&
            formulaReferencesField(cfg[fk], field))
          rows.push(row("blocking", "formula",
            `${fk} on a table column of ${host} references '${field.displayName}'`));
      }
    }
  });

  for (const m of canvas.menus || []) {
    if (!m) continue;
    for (const [where, cols] of [
      ["listing", m.list && m.list.columns],
      ["mobile listing", m.mobileListDesign && m.mobileListDesign.columns],
    ]) {
      for (const c of cols || []) {
        if (c && !c.system && (sameId(c.id, field.id) || (field.fieldName && c.fieldName === field.fieldName)))
          rows.push(row("blocking", "column", `${where} column on '${m.name || m.id}'`));
      }
    }
    for (const [fname, group] of [
      ["filter", m.filter], ["default filter", m.defaultFilter], ["app filter", m.defaultAppFilter],
    ]) {
      walkFilterItems(group, (it) => {
        if (!it.system && (sameId(it.fieldId, field.id) || (field.fieldName && it.fieldName === field.fieldName)))
          rows.push(row("blocking", "filter", `${fname} condition on '${m.name || m.id}'`));
      });
    }
  }

  for (const t of canvas.triggers || []) {
    if (!t) continue;
    for (const c of t.conditions || []) {
      if (c && (sameId(c.field, field.id) || (field.fieldName && c.field === field.fieldName)))
        rows.push(row("blocking", "trigger", `condition on automation '${t.name || t.id}'`));
    }
    if (t.type === "information" && (sameId(t.item, field.id) || (field.fieldName && t.item === field.fieldName)))
      rows.push(row("blocking", "trigger", `legacy automation '${t.name || t.id}' watches this field`));
  }

  /* import is read-only for ops (its config carries executable code), so these referrers
     cannot be cascaded in-proposal — same human-pre-step shape as authorizations, even
     though the reference graph classes them blocking. */
  for (const c of (canvas.import && canvas.import.columns) || []) {
    if (c && (sameId(c.informationId, field.id) ||
        (field.fieldName && c.fieldName === field.fieldName) ||
        (field.displayName && c.information === field.displayName)))
      rows.push(row("manual", "import",
        `import column '${c.header || c.id}' maps to '${field.displayName}' — adjust the import configuration in the designer first`));
  }

  for (const a of canvas.authorizations || []) {
    if (!a) continue;
    if (a.authorizationType === "information" && sameId(a.authorizationUnitId, field.id))
      rows.push(row("manual", "authorization",
        `a field-level permission rule targets '${field.displayName}' — remove it in the designer first (D8)`));
    if (a.entityType === 7 && typeof a.entityId === "string" && a.entityId.split(":").pop() === String(field.id))
      rows.push(row("manual", "authorization",
        `a ProfileField permission rule matches on '${field.displayName}' — remove it in the designer first (D8)`));
  }

  if (typeof canvas.title === "string" && field.fieldName &&
      extractFormulaRefs(canvas.title).fieldNames.has(field.fieldName))
    rows.push(row("warn", "title", `the entry-title template uses '${field.displayName}'`));
  if (canvas.task && (sameId(canvas.task.assignTo, field.id) || sameId(canvas.task.dueDate, field.id)))
    rows.push(row("warn", "task", "the (vestigial) task config points at this field"));

  rows.push(row("warn", "stored-values",
    `entry values stored under '${field.fieldName || field.id}' remain in existing entries (data is never erased by a canvas edit)`));
  if (moduleHasScripts(canvas))
    rows.push(row("warn", "scripts",
      "this module carries scripts the engine cannot scan — they may reference the field by name"));
  return rows;
};

const sweepSubField = (canvas, parentField, sub) => {
  const rows = [];
  walkDesigns(canvas, (item, host) => {
    if (item.custom || !sameId(item.id, parentField.id)) return;
    for (const cfg of item.fields || []) {
      if (cfg && sameId(cfg.id, sub.id))
        rows.push(row("blocking", "design-item", `table column config on ${host}`));
    }
  });
  walkDesigns(canvas, (item, host) => {
    for (const fk of FORMULA_KEYS) {
      if (typeof item[fk] === "string" && item[fk] &&
          formulaReferencesSubField(item[fk], parentField, sub))
        rows.push(row("blocking", "formula", `${fk} on ${host} references column '${sub.displayName}'`));
    }
    for (const cfg of item.fields || []) {
      for (const fk of FORMULA_KEYS) {
        if (cfg && typeof cfg[fk] === "string" && cfg[fk] &&
            formulaReferencesSubField(cfg[fk], parentField, sub))
          rows.push(row("blocking", "formula", `${fk} on a table column of ${host} references '${sub.displayName}'`));
      }
    }
  });
  for (const m of canvas.menus || []) {
    if (!m) continue;
    for (const cols of [m.list && m.list.columns, m.mobileListDesign && m.mobileListDesign.columns]) {
      for (const c of cols || []) {
        if (!c || c.system) continue;
        const isParent = sameId(c.id, parentField.id) ||
          (parentField.fieldName && c.fieldName === parentField.fieldName);
        if (!isParent) continue;
        for (const sc of c.fields || []) {
          if (sc && ((sub.fieldName && sc.fieldName === sub.fieldName) || sc.displayName === sub.displayName))
            rows.push(row("blocking", "column", `sub-column on listing '${m.name || m.id}'`));
        }
      }
    }
  }
  for (const a of canvas.authorizations || []) {
    if (a && sameId(a.authorizationUnitId, sub.id))
      rows.push(row("manual", "authorization",
        `a permission rule targets sub-field '${sub.displayName}' — remove it in the designer first (D8)`));
  }
  rows.push(row("warn", "stored-values",
    `table rows keep their '${sub.fieldName || sub.id}' values in existing entries`));
  return rows;
};

const sweepOption = (canvas, holder, option) => {
  const rows = [];
  walkDesigns(canvas, (item, host) => {
    if (item.custom || !sameId(item.id, holder.id)) return;
    if (Array.isArray(item.options) && item.options.indexOf(option.name) >= 0)
      rows.push(row("blocking", "design-option",
        `the form on ${host} offers '${option.name}' in its option subset`));
  });
  for (const m of canvas.menus || []) {
    if (!m) continue;
    walkFilterItems(m.filter, (it) => {
      if ((sameId(it.fieldId, holder.id) || (holder.fieldName && it.fieldName === holder.fieldName)) &&
          JSON.stringify(it.payload || null).indexOf(JSON.stringify(option.name)) >= 0)
        rows.push(row("warn", "filter-payload",
          `a filter on '${m.name || m.id}' compares against '${option.name}'`));
    });
  }
  rows.push(row("warn", "stored-values",
    `entries that selected '${option.name}' keep that value`));
  return rows;
};

const sweepState = (canvas, state) => {
  const rows = [];
  for (const fl of canvas.flows || []) {
    if (!fl) continue;
    if (sameId(fl.fromStateId, state.id) || sameId(fl.toStateId, state.id))
      rows.push(row("blocking", "flow", `flow '${fl.id}' ${sameId(fl.fromStateId, state.id) ? "leaves" : "enters"} '${state.name}'`));
    for (const cs of fl.conditionalStates || []) {
      if (cs && sameId(cs.toStateId, state.id))
        rows.push(row("blocking", "flow-conditional", `a conditional route on flow '${fl.id}' targets '${state.name}'`));
    }
  }
  for (const t of canvas.triggers || []) {
    if (!t) continue;
    if (sameId(t.stateId, state.id) ||
        (t.type === "state" && sameId(t.item, state.id)) ||
        sameId(t.conditionState, state.id))
      rows.push(row("blocking", "trigger", `automation '${t.name || t.id}' fires on '${state.name}'`));
  }
  if (canvas.import && sameId(canvas.import.initialStateId, state.id))
    rows.push(row("manual", "import",
      `the import configuration starts entries in '${state.name}' — adjust it in the designer first`));
  if (canvas.archive && Array.isArray(canvas.archive.states) &&
      canvas.archive.states.some((sid) => sameId(sid, state.id)))
    rows.push(row("blocking", "archive", `the archive configuration includes '${state.name}'`));
  rows.push(row("warn", "entries",
    `entries currently in '${state.name}' keep that state until they are moved`));
  return rows;
};

const sweepActivity = (canvas, act) => {
  const rows = [];
  for (const fl of canvas.flows || []) {
    if (fl && sameId(fl.activityId, act.id))
      rows.push(row("blocking", "flow", `flow '${fl.id}' is driven by '${act.name}'`));
  }
  for (const t of canvas.triggers || []) {
    if (!t) continue;
    if (sameId(t.activityId, act.id) || (t.type === "activity" && sameId(t.item, act.id)))
      rows.push(row("blocking", "trigger", `automation '${t.name || t.id}' fires on '${act.name}'`));
  }
  for (const a of canvas.authorizations || []) {
    if (a && a.authorizationType === "activity" && sameId(a.authorizationUnitId, act.id))
      rows.push(row("manual", "authorization",
        `a permission rule targets activity '${act.name}' — remove it in the designer first (D8)`));
  }
  if (canvas.formulaCache && !Array.isArray(canvas.formulaCache) && canvas.formulaCache[act.id])
    rows.push(row("warn", "formula-cache",
      "the server-stamped formula cache holds an entry for this activity (regenerated at save)"));
  return rows;
};

const sweepMenu = (canvas, menu) => {
  const rows = [];
  for (const [label, list] of [
    ["activity", canvas.activities], ["form", canvas.standardActivities], ["action button", canvas.actions],
  ]) {
    for (const entry of list || []) {
      if (entry && (entry.menus || []).some((mid) => sameId(mid, menu.id)))
        rows.push(row("blocking", "scoping",
          `${label} '${entry.name || entry.id}' is scoped to listing '${menu.name}'`));
    }
  }
  for (const a of canvas.authorizations || []) {
    if (!a) continue;
    if (a.authorizationType === "menu" && sameId(a.authorizationUnitId, menu.id))
      rows.push(row("manual", "authorization",
        `a permission rule targets listing '${menu.name}' — remove it in the designer first (D8)`));
    if (a.entityType === 6 && typeof a.entityId === "string" && a.entityId.split(":").pop() === String(menu.id))
      rows.push(row("manual", "authorization",
        `a ProfileListing permission rule matches listing '${menu.name}' — remove it in the designer first (D8)`));
  }
  return rows;
};

const sweepDocument = (canvas, doc) => {
  const rows = [];
  for (const a of canvas.activities || []) {
    if (a && sameId(a.documentId, doc.id))
      rows.push(row("blocking", "activity-document", `activity '${a.name}' generates '${doc.name || doc.id}'`));
  }
  for (const t of canvas.triggers || []) {
    if (t && sameId(t.documentId, doc.id))
      rows.push(row("blocking", "trigger-document",
        `legacy automation '${t.name || t.id}' attaches '${doc.name || doc.id}'`));
  }
  for (const a of canvas.authorizations || []) {
    if (a && a.authorizationType === "document" && sameId(a.authorizationUnitId, doc.id))
      rows.push(row("manual", "authorization",
        `a permission rule targets document '${doc.name || doc.id}' — remove it in the designer first (D8)`));
  }
  return rows;
};

const sweepAction = (canvas, action) => {
  const rows = [];
  for (const a of canvas.authorizations || []) {
    if (a && a.authorizationType === "action" && sameId(a.authorizationUnitId, action.id))
      rows.push(row("manual", "authorization",
        `a permission rule targets action '${action.name || action.id}' — remove it in the designer first (D8)`));
  }
  return rows;
};

/* Sweep dispatcher. `parents` = resolved chain nodes (for units whose sweep needs the
   parent, e.g. options and sub-fields). Leaves (rows, items, columns, filter items,
   flows, conditionalStates, aiActions) have no intra-canvas inbound references by
   construction — no sweep (x-reference-graph "leaves"). */
const sweepRemoveUnit = (canvas, unitName, node, parents) => {
  switch (unitName) {
    case "informationField": return sweepInformationField(canvas, node);
    case "subField": return sweepSubField(canvas, parents[parents.length - 1], node);
    case "option": return sweepOption(canvas, parents[parents.length - 1], node);
    case "state": return sweepState(canvas, node);
    case "activity": return sweepActivity(canvas, node);
    case "menu": return sweepMenu(canvas, node);
    case "document": return sweepDocument(canvas, node);
    case "action": return sweepAction(canvas, node);
    default: return [];
  }
};

/* ---- apply mutations + inverses ------------------------------------------------------- */

const siblingAnchor = (collection, index, idKey) => {
  const next = collection[index + 1];
  if (next) return { before: next[idKey] };
  const prev = collection[index - 1];
  if (prev) return { after: prev[idKey] };
  return {};
};

const insertAt = (collection, idKey, item, anchors, warnings) => {
  let index = collection.length;
  if (anchors && anchors.before != null) {
    const at = collection.findIndex((m) => m && sameId(m[idKey], anchors.before));
    if (at >= 0) index = at;
    else warnings.push(warn("P1", "anchor-gone",
      `anchor '${anchors.before}' no longer exists — appended instead`));
  } else if (anchors && anchors.after != null) {
    const at = collection.findIndex((m) => m && sameId(m[idKey], anchors.after));
    if (at >= 0) index = at + 1;
    else warnings.push(warn("P1", "anchor-gone",
      `anchor '${anchors.after}' no longer exists — appended instead`));
  }
  collection.splice(index, 0, item);
  return index;
};

/* ---- the pipeline --------------------------------------------------------------------- */

/**
 * Validate and apply a proposal (an ordered op list) against `canvas`.
 * Returns { ok, canvas, record, ops, errors, warnings }:
 *  - ok:true  → `canvas` is a NEW object with all ops applied; `record` carries per-op
 *               inverses (reverse order) + written snapshots for D12 revert/conflicts.
 *  - ok:false → `canvas`/`record` null; per-op errors in `ops[i].errors`, proposal-level
 *               ones in `errors`. The input canvas is never touched either way.
 * context: { source, label, dryRun, now, formulaKnownNames:Set, moduleIds:Set }.
 */
export const applyProposal = (canvas, ops, context) => {
  context = context || {};
  const results = ops.map(() => ({ ok: true, errors: [], warnings: [] }));
  const proposal = { ok: false, canvas: null, record: null, ops: results, errors: [], warnings: [] };

  if (!canvas || typeof canvas !== "object") {
    proposal.errors.push(err("P0", "admission", "no draft canvas loaded"));
    return proposal;
  }
  if (canvas.app != null) {
    proposal.errors.push(err("P0", "app-lock",
      "this is an installed app module — its canvas is managed by the app and not editable through ops"));
    return proposal;
  }
  if (!Array.isArray(ops) || !ops.length) {
    proposal.errors.push(err("P0", "admission", "no ops in the proposal"));
    return proposal;
  }

  const working = cloneDeep(canvas);
  const preViolations = collectViolations(working);
  const inverses = [];
  const written = [];
  const executed = [];
  const formulaWrites = [];
  const writtenNodes = [];

  for (let i = 0; i < ops.length; i++) {
    const op = cloneDeep(ops[i]);
    const res = results[i];
    const fail = (e) => { res.errors.push(e); res.ok = false; };

    for (const e of grammarCheck(op)) fail(e);
    if (!res.ok) { inverses.push(null); written.push(null); executed.push(op); continue; }

    /* -- root-scalar set --------------------------------------------------------------- */
    if (op.op === "set" && op.address.length === 0) {
      if (UNIT_CATALOG.root.rootScalarKeys.indexOf(op.key) < 0) {
        fail(err("P2", "root-key",
          `'${op.key}' is not a settable root key (§4 units 1–4: ${UNIT_CATALOG.root.rootScalarKeys.join(", ")})`));
      } else if (tierOfSection(op.key) !== "open") {
        fail(err("P2", "tier", `root key '${op.key}' is ${tierOfSection(op.key)} — not settable`));
      } else if (!op.unset && op.value != null && SCHEMA.properties[op.key] &&
                 SCHEMA.properties[op.key].type && !typeOk(SCHEMA.properties[op.key].type, op.value)) {
        fail(err("P3", "type", `'${op.key}' should be ${[].concat(SCHEMA.properties[op.key].type).join("|")}`));
      } else {
        const prior = working[op.key];
        if (op.unset) delete working[op.key];
        else working[op.key] = cloneDeep(op.value);
        inverses.push(prior === undefined
          ? { op: "set", address: [], key: op.key, unset: true }
          : { op: "set", address: [], key: op.key, value: cloneDeep(prior) });
        written.push({ value: op.unset ? undefined : cloneDeep(op.value), unset: !!op.unset });
        executed.push(op);
      }
      if (!res.ok) { inverses.push(null); written.push(null); executed.push(op); }
      continue;
    }

    /* -- addressed ops ----------------------------------------------------------------- */
    const hit = resolveAddress(working, op.address, { forUpsert: op.op === "upsert" });
    if (hit.errors) {
      hit.errors.forEach(fail);
      inverses.push(null); written.push(null); executed.push(op);
      continue;
    }
    const unit = UNIT_CATALOG[hit.unitName];
    const idKey = unit.idKey;

    if (!unit.verbs[op.op]) {
      fail(err("P0", "verb",
        `'${op.op}' is not permitted on ${hit.unitName} (§4: ${Object.keys(unit.verbs).join("·")})`));
      inverses.push(null); written.push(null); executed.push(op);
      continue;
    }

    // parent-shape preconditions (slice units)
    const parentNode = hit.parentNode;
    if (hit.unitName === "subField" && [16, 17].indexOf(parentNode.type) < 0)
      fail(err("P4", "precondition",
        `'${parentNode.displayName || parentNode.id}' is not a Table/List field — it has no sub-fields`));
    if (hit.unitName === "option" && [14, 15].indexOf(parentNode.type) < 0)
      fail(err("P4", "precondition",
        `'${parentNode.displayName || parentNode.id}' is not a Select/Tag field — it has no options`));
    if (!res.ok) { inverses.push(null); written.push(null); executed.push(op); continue; }

    if (op.op === "set") {
      const target = hit.node;
      let tier = tierOfKey(hit.unitName, op.key);
      if (unit.setWhitelist && unit.setWhitelist.indexOf(op.key) < 0) {
        fail(err("P2", "set-whitelist",
          `'${op.key}' is not settable on ${hit.unitName} (allowed: ${unit.setWhitelist.join(", ")})`));
      } else if (tier === "identity") {
        fail(err("P2", "identity", `'${op.key}' is an identity key — immutable (remove + recreate instead)`));
      } else if (tier === "graph" && hit.unitName === "trigger") {
        // D19: a trigger's graph is ONE unit — replaced whole, validated as a whole
        const graphErrors = op.unset ? [] : validateGraphPayload(op.value, target[op.key]);
        if (graphErrors.length) {
          graphErrors.forEach(fail);
        } else {
          const prior = target[op.key];
          if (op.unset) delete target[op.key];
          else target[op.key] = cloneDeep(op.value);
          inverses.push(prior === undefined
            ? { op: "set", address: op.address, key: op.key, unset: true }
            : { op: "set", address: op.address, key: op.key, value: cloneDeep(prior) });
          written.push({ value: op.unset ? undefined : cloneDeep(op.value), unset: !!op.unset });
          executed.push(op);
          continue;
        }
      } else if (["code", "graph", "derived", "read-only"].indexOf(tier) >= 0) {
        fail(err("P2", "tier", `'${op.key}' is ${tier === "graph" ? "an automation graph" : tier} — not settable through ops`));
      } else {
        if (tier === "unknown")
          res.warnings.push(warn("P2", "unknown-key",
            `'${op.key}' is not a key the canvas schema describes on ${hit.unitName}`));
        if (tier === "formula" && op.value != null && op.value !== "")
          formulaWrites.push({ path: `${describeHops(op.address, op.address.length - 1)}.${op.key}`, value: op.value });
        const propDef = propertySchema(hit.unitName, op.key);
        if (propDef && !op.unset && op.value != null) {
          const shaped = { errors: [], warnings: [] };
          if (propDef.enum && propDef.enum.indexOf(op.value) < 0)
            shaped.errors.push(err("P3", "enum", `'${op.key}' must be one of ${propDef.enum.join(", ")}`));
          else if (propDef.type && !typeOk(propDef.type, op.value))
            shaped.errors.push(err("P3", "type",
              `'${op.key}' should be ${Array.isArray(propDef.type) ? propDef.type.join("|") : propDef.type}`));
          shaped.errors.forEach(fail);
          res.warnings.push(...shaped.warnings);
        }
        if (res.ok) {
          if (op.key === "aiFill" && !op.unset)
            writtenNodes.push({ i, unitName: hit.unitName, address: op.address });
          const prior = target[op.key];
          /* rename hazard: formulas and the title template reference fields by DISPLAY
             name — renaming leaves those tokens pointing at nothing. Same orphan class
             the remove sweep exists for; warn (a rename is legal, but say what breaks). */
          if (op.key === "displayName" &&
              (hit.unitName === "informationField" || hit.unitName === "subField") &&
              typeof prior === "string" && prior && prior !== op.value) {
            let hits = 0;
            walkDesigns(working, (item) => {
              for (const fk of FORMULA_KEYS) {
                if (typeof item[fk] === "string" && formulaReferencesField(item[fk], { displayName: prior }))
                  hits++;
                for (const cfg of item.fields || [])
                  if (cfg && typeof cfg[fk] === "string" && formulaReferencesField(cfg[fk], { displayName: prior }))
                    hits++;
              }
            });
            if (hits > 0)
              res.warnings.push(warn("P5", "rename-formula",
                `${hits} formula(s) reference '${prior}' by name — update them or they stop resolving`));
          }
          if (op.unset) delete target[op.key];
          else target[op.key] = cloneDeep(op.value);
          inverses.push(prior === undefined
            ? { op: "set", address: op.address, key: op.key, unset: true }
            : { op: "set", address: op.address, key: op.key, value: cloneDeep(prior) });
          written.push({ value: op.unset ? undefined : cloneDeep(op.value), unset: !!op.unset });
          executed.push(op);
          continue;
        }
      }
      inverses.push(null); written.push(null); executed.push(op);
      continue;
    }

    if (op.op === "upsert") {
      const replacing = hit.node != null;
      const payload = cloneDeep(op.item);

      // identity: the address is the identity source
      if (payload[idKey] == null) payload[idKey] = hit.id;
      if (!sameId(payload[idKey], hit.id))
        fail(err("P1", "identity",
          `payload ${idKey} '${payload[idKey]}' must equal the address id '${hit.id}'`));
      if (hit.unitName === "informationField" || hit.unitName === "subField") {
        if (replacing) {
          if (payload.fieldName == null) payload.fieldName = hit.node.fieldName;
          if (payload.fieldName !== hit.node.fieldName)
            fail(err("P2", "identity", "fieldName is the storage key — immutable"));
        } else {
          if (payload.fieldName == null) payload.fieldName = payload.id;
          if (payload.fieldName !== String(payload.id))
            fail(err("P2", "identity", "on create, fieldName must equal the new id (designer parity)"));
        }
      }
      if (unit.payloadDenied) {
        for (const k of unit.payloadDenied) {
          if (payload[k] !== undefined && !(replacing && isEqual(payload[k], hit.node[k])))
            fail(err("P2", "payload-denied",
              `'${k}' is not authorable through ops on ${hit.unitName} (bodies stay their dedicated lanes)`));
        }
      }
      // D19: trigger payload graphs are validated whole and bypass the generic graph-key
      // carry-over (which would refuse authored graphs) — extracted here, re-attached to
      // the merged node after the policy walk.
      const graphAttach = {};
      if (hit.unitName === "trigger") {
        for (const gk of ["automation", "automationBefore"]) {
          if (payload[gk] === undefined) continue;
          validateGraphPayload(payload[gk], replacing ? hit.node[gk] : null).forEach(fail);
          graphAttach[gk] = payload[gk];
          delete payload[gk];
        }
      }
      if (!res.ok) { inverses.push(null); written.push(null); executed.push(op); continue; }

      const out = { errors: [], warnings: [], formulaWrites };
      const def = definitionFor(hit.unitName);
      const merged = policyWalk(def, replacing ? hit.node : null, payload,
        replacing ? "replace" : "create", "", out);
      shapeCheck(def, merged, "", out, null);
      out.errors.forEach(fail);
      res.warnings.push(...out.warnings);
      if (!res.ok) { inverses.push(null); written.push(null); executed.push(op); continue; }
      for (const gk of Object.keys(graphAttach)) merged[gk] = cloneDeep(graphAttach[gk]);

      if (replacing) {
        const prior = cloneDeep(hit.node);
        hit.collection[hit.index] = merged;
        inverses.push({ op: "upsert", address: op.address, item: prior });
        written.push({ node: cloneDeep(merged) });
      } else {
        const anchors = { before: op.before, after: op.after };
        insertAt(hit.collection, idKey, merged, anchors, res.warnings);
        inverses.push({ op: "remove", address: op.address });
        written.push({ node: cloneDeep(merged) });
      }
      writtenNodes.push({ i, unitName: hit.unitName, address: op.address });
      executed.push(Object.assign({}, op, { item: cloneDeep(merged) }));
      continue;
    }

    if (op.op === "remove") {
      const prior = cloneDeep(hit.node);
      const anchor = siblingAnchor(hit.collection, hit.index, idKey);
      const scripts = countCodeValues(definitionFor(hit.unitName), hit.node);
      if (scripts > 0)
        res.warnings.push(warn("P2", "removes-code", `removing this ${hit.unitName} also removes ${scripts} script(s)`));
      hit.collection.splice(hit.index, 1);
      inverses.push(Object.assign({ op: "upsert", address: op.address, item: prior }, anchor));
      written.push({ removed: true });
      // sweep runs after ALL ops have applied (net state) — remember what to sweep for
      res._sweep = { unitName: hit.unitName, node: prior, parents: parentsFromChain(op, working) };
      executed.push(op);
      continue;
    }

    if (op.op === "move") {
      const fromIndex = hit.index;
      const priorAnchor = siblingAnchor(hit.collection, fromIndex, idKey);
      const anchorId = op.before != null ? op.before : op.after;
      const anchorIndex = hit.collection.findIndex((m) => m && sameId(m[idKey], anchorId));
      if (anchorIndex < 0 || sameId(anchorId, hit.id)) {
        fail(err("P1", "anchor", `move anchor '${anchorId}' is not a sibling`));
        inverses.push(null); written.push(null); executed.push(op);
        continue;
      }
      const [moved] = hit.collection.splice(fromIndex, 1);
      let target = hit.collection.findIndex((m) => m && sameId(m[idKey], anchorId));
      if (op.after != null) target += 1;
      hit.collection.splice(target, 0, moved);
      inverses.push(Object.assign({ op: "move", address: op.address }, priorAnchor));
      const at = hit.collection.indexOf(moved);
      written.push({
        prevId: at > 0 ? hit.collection[at - 1][idKey] : null,
        nextId: at < hit.collection.length - 1 ? hit.collection[at + 1][idKey] : null,
      });
      executed.push(op);
      continue;
    }
  }

  /* -- written-node forward checks (only nodes this proposal wrote) -------------------- */
  runWrittenChecks(working, writtenNodes, results);

  /* -- P4: delta invariants ------------------------------------------------------------ */
  const postViolations = collectViolations(working);
  for (const [key, message] of postViolations) {
    if (!preViolations.has(key))
      proposal.errors.push(err("P4", "invariant", message, { violation: key }));
  }
  checkFormulaWrites(formulaWrites, context, proposal);

  /* -- P5: net-state sweeps + forward refs --------------------------------------------- */
  for (let i = 0; i < results.length; i++) {
    const sweep = results[i]._sweep;
    if (!sweep) continue;
    delete results[i]._sweep;
    const rows = sweepRemoveUnit(working, sweep.unitName, sweep.node, sweep.parents);
    for (const r of rows) {
      if (r.kind === "blocking")
        results[i].errors.push(err("P5", "referrer", r.label, { referrer: r.code, cascadable: true }));
      else if (r.kind === "manual")
        results[i].errors.push(err("P5", "referrer-manual", r.label, { referrer: r.code, cascadable: false }));
      else results[i].warnings.push(warn("P5", r.code, r.label));
    }
    if (results[i].errors.length) results[i].ok = false;
  }
  if (context.moduleIds && context.moduleIds.size) {
    for (const f of working.information || []) {
      if (f && f.connectionId != null && f.connectionId !== "" &&
          !context.moduleIds.has(String(f.connectionId)))
        proposal.warnings.push(warn("P5", "connection-unresolved",
          `field '${f.displayName}' references module '${f.connectionId}' which is not in this workspace`));
    }
  }

  proposal.ok = results.every((r) => r.ok) && proposal.errors.length === 0;
  if (!proposal.ok || context.dryRun) return proposal;

  proposal.canvas = working;
  proposal.record = {
    id: "apply-" + Date.now().toString(36) + "-" + (++recordCounter),
    time: context.now != null ? context.now : Date.now(),
    source: context.source || "engine",
    label: context.label || describeProposal(executed),
    ops: executed,
    inverses: inverses.slice().reverse(),
    written,
  };
  return proposal;
};

/* Forward-resolution checks on nodes the proposal wrote, evaluated at net state.
   Scoped to written nodes only — pre-existing dangling references in old canvases must
   never block an unrelated edit (same philosophy as the delta invariants). */
const AIFILL_TYPES = [10, 11, 12, 13]; // Image/Images/File/Files
const LAYOUT_INPUTS = ["empty", "custom"];

const infoById = (canvas, id) =>
  (canvas.information || []).find((f) => f && sameId(f.id, id));

const isLayoutItem = (item) =>
  item.custom === true ||
  LAYOUT_INPUTS.indexOf(item.input) >= 0 ||
  item.header != null || item.content != null || item.url != null ||
  item.design != null || (Array.isArray(item.tabs) && item.tabs.length > 0);

const runWrittenChecks = (working, writtenNodes, results) => {
  for (const w of writtenNodes) {
    const res = results[w.i];
    const fail = (e) => { res.errors.push(e); res.ok = false; };
    const probe = resolveAddress(working, w.address, {});
    if (probe.errors || probe.node == null) continue; // a later op removed it — nothing to check
    const node = probe.node;

    if (w.unitName === "formItem") {
      if (!isLayoutItem(node) && node.id != null && !infoById(working, node.id)) {
        fail(err("P5", "design-field",
          `form element references field '${node.id}' which does not exist at net state`));
      }
      if (node.aiFill && node.aiFill.enabled !== false && node.id != null) {
        const field = infoById(working, node.id);
        if (field && AIFILL_TYPES.indexOf(field.type) < 0)
          fail(err("P5", "aifill-type",
            `GNey fill only applies to photo/file elements — '${field.displayName}' is not one`));
      }
      // option subsets must stay within the bound field's options
      if (Array.isArray(node.options) && node.options.length && node.id != null) {
        const field = infoById(working, node.id);
        if (field && Array.isArray(field.optionList)) {
          const names = new Set(field.optionList.filter(Boolean).map((o) => o.name));
          for (const name of node.options) {
            if (!names.has(name))
              fail(err("P5", "design-option",
                `the form offers option '${name}' which '${field.displayName}' does not define`));
          }
        }
      }
    }

    if (w.unitName === "itemSubfieldConfig") {
      const parentProbe = resolveAddress(working, w.address.slice(0, -1), {});
      const parentItem = parentProbe.errors ? null : parentProbe.node;
      const field = parentItem ? infoById(working, parentItem.id) : null;
      if (field && !(field.fields || []).some((s) => s && sameId(s.id, node.id)))
        fail(err("P5", "subfield-config",
          `'${field.displayName}' has no sub-field '${node.id}' for this column config`));
    }

    if (w.unitName === "listColumn" || w.unitName === "mobileColumn") {
      if (!node.system && node.id != null && !infoById(working, node.id) &&
          !(working.information || []).some((f) => f && node.fieldName && f.fieldName === node.fieldName))
        fail(err("P5", "column-field",
          `column '${node.displayName || node.id}' references a field that does not exist`));
    }

    if (w.unitName === "filterItem" && node.type !== "or") {
      if (!node.system && node.fieldId != null && !infoById(working, node.fieldId) &&
          !(working.information || []).some((f) => f && node.fieldName && f.fieldName === node.fieldName))
        fail(err("P5", "filter-field",
          `filter condition '${node.id}' references a field that does not exist`));
    }
  }
};

const parentsFromChain = (op, working) => {
  const parents = [];
  for (let h = 0; h < op.address.length - 1; h++) {
    const step = resolveAddress(working, op.address.slice(0, h + 1), {});
    if (step && !step.errors) parents.push(step.node);
  }
  return parents;
};

/* Dry-run: full pipeline, no mutation escapes. */
export const validateProposal = (canvas, ops, context) =>
  applyProposal(canvas, ops, Object.assign({}, context || {}, { dryRun: true }));

/* Guard entry point (D11): sweep one node for referrers WITHOUT applying anything.
   Returns { unitName, node, rows } or { errors }. */
export const sweepRemove = (canvas, address) => {
  const hit = resolveAddress(canvas, address, {});
  if (hit.errors) return { errors: hit.errors };
  return {
    unitName: hit.unitName,
    node: hit.node,
    rows: sweepRemoveUnit(canvas, hit.unitName, hit.node, parentsFromChain({ address }, canvas)),
  };
};

/* ---- D12: revert composition --------------------------------------------------------- */

/**
 * Compose a record's inverses into an ordinary proposal, with written-vs-current conflict
 * detection. Returns { ops, rows: [{ op, conflict, reason }] } — rows in apply order
 * (record.inverses is already reverse-of-execution). Conflicted rows default UNCHECKED in
 * the card; applying the checked subset goes through applyProposal like any proposal.
 */
export const buildRevertProposal = (canvas, record) => {
  const rows = [];
  const n = record.ops.length;
  for (let k = 0; k < record.inverses.length; k++) {
    const inverse = cloneDeep(record.inverses[k]);
    const originalIndex = n - 1 - k;
    const w = record.written[originalIndex];
    const original = record.ops[originalIndex];
    let conflict = false;
    let reason = null;

    if (inverse == null) continue; // op never executed (refused proposal — shouldn't be recorded)

    const probe = original.address.length
      ? resolveAddress(canvas, original.address, {})
      : null;
    const currentNode = probe && !probe.errors ? probe.node : null;

    if (original.op === "set") {
      const holder = original.address.length ? currentNode : canvas;
      if (holder == null) { conflict = true; reason = "the node this wrote to no longer exists"; }
      else {
        const cur = holder[original.key];
        const matches = w.unset ? cur === undefined : isEqual(cur, w.value);
        if (!matches) { conflict = true; reason = `'${original.key}' was changed after this apply`; }
      }
    } else if (original.op === "upsert") {
      if (currentNode == null) { conflict = true; reason = "the node was removed after this apply"; }
      else if (!isEqual(currentNode, w.node)) { conflict = true; reason = "the node was edited after this apply"; }
    } else if (original.op === "remove") {
      if (currentNode != null) { conflict = true; reason = "something now exists at this address again"; }
    } else if (original.op === "move") {
      if (currentNode == null) { conflict = true; reason = "the node no longer exists"; }
      else if (probe && !probe.errors) {
        const coll = probe.collection;
        const at = probe.index;
        const idKey = UNIT_CATALOG[probe.unitName].idKey;
        const prevId = at > 0 ? coll[at - 1][idKey] : null;
        const nextId = at < coll.length - 1 ? coll[at + 1][idKey] : null;
        if (!sameId(prevId, w.prevId) && !(prevId == null && w.prevId == null) ||
            !sameId(nextId, w.nextId) && !(nextId == null && w.nextId == null)) {
          conflict = true; reason = "the collection was reordered after this apply";
        }
      }
    }

    rows.push({ op: inverse, conflict, reason });
  }
  return { ops: rows.map((r) => r.op), rows };
};

/* ---- D11: cascade composition for the guard dialog ------------------------------------ */

/**
 * For a pending remove, compose the explicit cascade as checklist rows.
 * Returns { unitName, node, target, rows } or { errors }:
 *  - target: the remove op itself.
 *  - rows: [{ kind, label, op?, authRow?, checkable, defaultChecked }]
 *    kind 'blocking' → cascade op, pre-checked (refused without it)
 *    kind 'formula'  → set-op clearing a referencing formula, UNCHECKED by default
 *                      (clearing changes behavior — the human opts in)
 *    kind 'auth'     → authorization row, removable by the HUMAN lane only (D8/D11
 *                      asymmetry); handled store-side, not an engine op
 *    kind 'notice'   → informational (warn rows, import/human pre-steps) — not checkable.
 * Everything op-shaped re-validates through applyProposal at apply time, so a composition
 * gap surfaces as a named refusal, never a silent orphan.
 */
export const composeCascade = (canvas, address) => {
  const hit = resolveAddress(canvas, address, {});
  if (hit.errors) return { errors: hit.errors };
  const unitName = hit.unitName;
  const node = hit.node;
  const rows = [];
  const seenOps = new Set();
  const pushOp = (kind, label, op, defaultChecked) => {
    const key = JSON.stringify([op.op, op.address, op.key || null]);
    if (seenOps.has(key)) return;
    seenOps.add(key);
    rows.push({ kind, label, op, checkable: true, defaultChecked: defaultChecked !== false });
  };
  const pushAuth = (label, authRow) =>
    rows.push({ kind: "auth", label, authRow, checkable: true, defaultChecked: true });
  const pushNotice = (label) =>
    rows.push({ kind: "notice", label, checkable: false, defaultChecked: false });

  const sweepRows = sweepRemoveUnit(canvas, unitName, node, parentsFromChain({ address }, canvas));

  if (unitName === "informationField" || unitName === "subField") {
    const field = unitName === "subField" ? hit.parentNode : node;
    const sub = unitName === "subField" ? node : null;

    walkDesigns(canvas, (item, host, itemAddress) => {
      if (sub) {
        if (!item.custom && sameId(item.id, field.id)) {
          for (const cfg of item.fields || []) {
            if (cfg && sameId(cfg.id, sub.id))
              pushOp("blocking", `Remove the '${sub.displayName}' column config from ${host}`,
                { op: "remove", address: itemAddress.concat([["fields", cfg.id]]) });
          }
        }
      } else if (!item.custom && sameId(item.id, field.id)) {
        pushOp("blocking", `Remove the form element on ${host}`,
          { op: "remove", address: itemAddress });
      }
      const target = sub || field;
      const referenced = (formula) => sub
        ? formulaReferencesSubField(formula, field, sub)
        : formulaReferencesField(formula, field);
      /* formula referrers are blocking-class (x-reference-graph) — the checklist row is
         pre-checked like any cascade op, visually distinct so the human sees behavior
         change (a cleared formula ≠ a removed layout element) */
      for (const fk of FORMULA_KEYS) {
        if (typeof item[fk] === "string" && item[fk] && referenced(item[fk]))
          pushOp("formula", `Clear the ${fk} on ${host} (it references '${target.displayName}')`,
            { op: "set", address: itemAddress, key: fk, unset: true });
      }
    });

    if (!sub) {
      for (const m of canvas.menus || []) {
        if (!m) continue;
        for (const [seg, cols] of [
          ["list.columns", m.list && m.list.columns],
          ["mobileListDesign.columns", m.mobileListDesign && m.mobileListDesign.columns],
        ]) {
          for (const c of cols || []) {
            if (c && !c.system && (sameId(c.id, field.id) || (field.fieldName && c.fieldName === field.fieldName)))
              pushOp("blocking", `Remove the column from listing '${m.name || m.id}'`,
                { op: "remove", address: [["menus", m.id], [seg, c.id]] });
          }
        }
        for (const [seg, group] of [
          ["filter.items", m.filter], ["defaultFilter.items", m.defaultFilter], ["defaultAppFilter.items", m.defaultAppFilter],
        ]) {
          walkFilterItems(group, (it, itemAddress) => {
            if (!it.system && (sameId(it.fieldId, field.id) || (field.fieldName && it.fieldName === field.fieldName)))
              pushOp("blocking", `Remove the filter condition on '${m.name || m.id}'`,
                { op: "remove", address: itemAddress });
          }, [["menus", m.id]], seg);
        }
      }
      for (const t of canvas.triggers || []) {
        if (!t) continue;
        const hitsCondition = (t.conditions || []).some((c) =>
          c && (sameId(c.field, field.id) || (field.fieldName && c.field === field.fieldName)));
        const legacyWatch = t.type === "information" &&
          (sameId(t.item, field.id) || (field.fieldName && t.item === field.fieldName));
        if (hitsCondition || legacyWatch)
          pushOp("blocking", `Remove automation '${t.name || t.id}' (it watches this field)`,
            { op: "remove", address: [["triggers", t.id]] });
      }
    } else {
      for (const m of canvas.menus || []) {
        if (!m) continue;
        for (const [seg, cols] of [
          ["list.columns", m.list && m.list.columns],
          ["mobileListDesign.columns", m.mobileListDesign && m.mobileListDesign.columns],
        ]) {
          for (const c of cols || []) {
            if (!c || c.system) continue;
            const isParent = sameId(c.id, field.id) || (field.fieldName && c.fieldName === field.fieldName);
            if (!isParent || !(c.fields || []).some((sc) =>
              sc && ((sub.fieldName && sc.fieldName === sub.fieldName) || sc.displayName === sub.displayName))) continue;
            const trimmed = cloneDeep(c);
            trimmed.fields = c.fields.filter((sc) =>
              !(sc && ((sub.fieldName && sc.fieldName === sub.fieldName) || sc.displayName === sub.displayName)));
            pushOp("blocking", `Drop the sub-column from listing '${m.name || m.id}'`,
              { op: "upsert", address: [["menus", m.id], [seg, c.id]], item: trimmed });
          }
        }
      }
    }
  }

  if (unitName === "option") {
    const holder = hit.parentNode;
    walkDesigns(canvas, (item, host, itemAddress) => {
      if (item.custom || !sameId(item.id, holder.id)) return;
      if (Array.isArray(item.options) && item.options.indexOf(node.name) >= 0)
        pushOp("blocking", `Drop '${node.name}' from the option subset on ${host}`,
          { op: "set", address: itemAddress, key: "options", value: item.options.filter((n) => n !== node.name) });
    });
  }

  if (unitName === "state") {
    const removedFlows = new Set();
    for (const fl of canvas.flows || []) {
      if (!fl) continue;
      if (sameId(fl.fromStateId, node.id) || sameId(fl.toStateId, node.id)) {
        removedFlows.add(String(fl.id));
        pushOp("blocking", `Remove flow '${fl.id}' (${sameId(fl.fromStateId, node.id) ? "leaves" : "enters"} '${node.name}')`,
          { op: "remove", address: [["flows", fl.id]] });
      }
    }
    for (const fl of canvas.flows || []) {
      if (!fl || removedFlows.has(String(fl.id))) continue;
      for (const cs of fl.conditionalStates || []) {
        if (cs && sameId(cs.toStateId, node.id))
          pushOp("blocking", `Remove the conditional route to '${node.name}' on flow '${fl.id}'`,
            { op: "remove", address: [["flows", fl.id], ["conditionalStates", cs.id]] });
      }
    }
    for (const t of canvas.triggers || []) {
      if (!t) continue;
      if (sameId(t.stateId, node.id) || (t.type === "state" && sameId(t.item, node.id)) || sameId(t.conditionState, node.id))
        pushOp("blocking", `Remove automation '${t.name || t.id}' (fires on '${node.name}')`,
          { op: "remove", address: [["triggers", t.id]] });
    }
    if (canvas.archive && Array.isArray(canvas.archive.states) &&
        canvas.archive.states.some((sid) => sameId(sid, node.id))) {
      const trimmed = cloneDeep(canvas.archive);
      trimmed.states = canvas.archive.states.filter((sid) => !sameId(sid, node.id));
      pushOp("blocking", `Drop '${node.name}' from the archive configuration`,
        { op: "set", address: [], key: "archive", value: trimmed });
    }
  }

  if (unitName === "activity") {
    for (const fl of canvas.flows || []) {
      if (fl && sameId(fl.activityId, node.id))
        pushOp("blocking", `Remove flow '${fl.id}' (driven by '${node.name}')`,
          { op: "remove", address: [["flows", fl.id]] });
    }
    for (const t of canvas.triggers || []) {
      if (!t) continue;
      if (sameId(t.activityId, node.id) || (t.type === "activity" && sameId(t.item, node.id)))
        pushOp("blocking", `Remove automation '${t.name || t.id}' (fires on '${node.name}')`,
          { op: "remove", address: [["triggers", t.id]] });
    }
  }

  if (unitName === "menu") {
    for (const [section, list] of [
      ["activities", canvas.activities], ["standardActivities", canvas.standardActivities], ["actions", canvas.actions],
    ]) {
      for (const entry of list || []) {
        if (entry && (entry.menus || []).some((mid) => sameId(mid, node.id)))
          pushOp("blocking", `Unscope '${entry.name || entry.id}' from listing '${node.name}'`,
            { op: "set", address: [[section, entry.id]], key: "menus",
              value: entry.menus.filter((mid) => !sameId(mid, node.id)) });
      }
    }
  }

  if (unitName === "document") {
    for (const a of canvas.activities || []) {
      if (a && sameId(a.documentId, node.id))
        pushOp("blocking", `Unbind '${node.name || node.id}' from activity '${a.name}'`,
          { op: "set", address: [["activities", a.id]], key: "documentId", unset: true });
    }
    for (const t of canvas.triggers || []) {
      if (t && sameId(t.documentId, node.id))
        pushOp("blocking", `Unbind '${node.name || node.id}' from automation '${t.name || t.id}'`,
          { op: "set", address: [["triggers", t.id]], key: "documentId", unset: true });
    }
  }

  /* authorization referrers — human-cascadable (D11), engine-untouchable (D8): the store
     removes them outside the op lane and records them for revert */
  for (const a of canvas.authorizations || []) {
    if (!a) continue;
    const matches =
      (unitName === "informationField" &&
        ((a.authorizationType === "information" && sameId(a.authorizationUnitId, node.id)) ||
         (a.entityType === 7 && typeof a.entityId === "string" && a.entityId.split(":").pop() === String(node.id)))) ||
      (unitName === "subField" && sameId(a.authorizationUnitId, node.id)) ||
      (unitName === "activity" && a.authorizationType === "activity" && sameId(a.authorizationUnitId, node.id)) ||
      (unitName === "menu" &&
        ((a.authorizationType === "menu" && sameId(a.authorizationUnitId, node.id)) ||
         (a.entityType === 6 && typeof a.entityId === "string" && a.entityId.split(":").pop() === String(node.id)))) ||
      (unitName === "document" && a.authorizationType === "document" && sameId(a.authorizationUnitId, node.id)) ||
      (unitName === "action" && a.authorizationType === "action" && sameId(a.authorizationUnitId, node.id));
    if (matches)
      pushAuth(`Remove the permission rule for ${a.entityName || a.entity || a.entityname || "a profile"} on this ${unitName === "informationField" ? "field" : unitName}`, a);
  }

  for (const r of sweepRows) {
    if (r.kind === "warn") pushNotice(r.label);
    if (r.kind === "manual" && r.code === "import") pushNotice(r.label);
  }

  const order = { blocking: 0, formula: 1, auth: 2, notice: 3 };
  rows.sort((x, y) => order[x.kind] - order[y.kind]);
  return { unitName, node, target: { op: "remove", address }, rows };
};

/* ---- P4: whole-draft audit ------------------------------------------------------------ */

/**
 * The validate-draft report: an ABSOLUTE integrity audit of the canvas (unlike the
 * pipeline's delta-checked invariants, which only refuse what a proposal introduces).
 * Combines the invariant collector with the dangling-reference scans that otherwise only
 * run on written nodes — the live twin of the server's report-only save-time consistency
 * check. Returns [{ severity: 'error'|'warn', code, label }].
 */
export const auditCanvas = (canvas) => {
  const rows = [];
  if (!canvas || typeof canvas !== "object") return rows;

  for (const [, message] of collectViolations(canvas))
    rows.push({ severity: "error", code: "invariant", label: message });

  const fieldById = new Map();
  for (const f of canvas.information || [])
    if (f && f.id != null) fieldById.set(String(f.id), f);
  const fieldByName = new Map();
  for (const f of canvas.information || [])
    if (f && f.fieldName) fieldByName.set(f.fieldName, f);

  walkDesigns(canvas, (item, host) => {
    if (!item.custom && !isLayoutItem(item) && item.id != null && !fieldById.has(String(item.id))) {
      rows.push({ severity: "error", code: "design-field",
        label: `a form element on ${host} references field '${item.id}' which does not exist` });
      return;
    }
    const field = item.id != null ? fieldById.get(String(item.id)) : null;
    if (field && Array.isArray(item.options) && item.options.length && Array.isArray(field.optionList)) {
      const names = new Set(field.optionList.filter(Boolean).map((o) => o.name));
      for (const name of item.options) {
        if (!names.has(name))
          rows.push({ severity: "error", code: "design-option",
            label: `the form on ${host} offers option '${name}' which '${field.displayName}' does not define` });
      }
    }
    if (field && Array.isArray(item.fields)) {
      const subIds = new Set((field.fields || []).filter(Boolean).map((s) => String(s.id)));
      for (const cfg of item.fields) {
        if (cfg && cfg.id != null && !subIds.has(String(cfg.id)))
          rows.push({ severity: "error", code: "subfield-config",
            label: `a column config on ${host} references sub-field '${cfg.id}' which '${field.displayName}' does not have` });
      }
    }
    if (field && item.aiFill && item.aiFill.enabled !== false &&
        AIFILL_TYPES.indexOf(field.type) < 0)
      rows.push({ severity: "warn", code: "aifill-type",
        label: `GNey fill on ${host} targets '${field.displayName}', which is not a photo/file field` });
  });

  for (const m of canvas.menus || []) {
    if (!m) continue;
    for (const [where, cols] of [
      ["listing", m.list && m.list.columns],
      ["mobile listing", m.mobileListDesign && m.mobileListDesign.columns],
    ]) {
      for (const c of cols || []) {
        if (c && !c.system && c.id != null &&
            !fieldById.has(String(c.id)) && !(c.fieldName && fieldByName.has(c.fieldName)))
          rows.push({ severity: "error", code: "column-field",
            label: `${where} column '${c.displayName || c.id}' on '${m.name || m.id}' references a field that does not exist` });
      }
    }
    for (const [fname, group] of [
      ["filter", m.filter], ["default filter", m.defaultFilter], ["app filter", m.defaultAppFilter],
    ]) {
      walkFilterItems(group, (it) => {
        if (!it.system && it.fieldId != null &&
            !fieldById.has(String(it.fieldId)) && !(it.fieldName && fieldByName.has(it.fieldName)))
          rows.push({ severity: "error", code: "filter-field",
            label: `a ${fname} condition on '${m.name || m.id}' references a field that does not exist` });
      });
    }
  }

  return rows;
};

/* ---- D19: automation graphs as UOA units ----------------------------------------------- */

/**
 * A block graph is ONE unit of alteration: replaced whole, never partially edited.
 * Validation of an incoming graph against the one it replaces, per the persisted
 * contract the runner consumes (AutomationBlockRunner/FlowBlock): { blocks: { id:
 * { name, data, inputs.input_1.connections[].node, outputs.output_1.connections[].node
 * } } }, at least one 'start' block, wiring resolving to real block ids — and the D7
 * line intact INSIDE the unit: script-bearing keys in block data must carry the OLD
 * graph's value verbatim or be absent; authoring them refuses.
 */
export const validateGraphPayload = (newGraph, oldGraph) => {
  const errors = [];
  if (newGraph == null) return errors; // clearing a graph is legal (set unset handles it)
  if (typeof newGraph !== "object" || Array.isArray(newGraph) ||
      newGraph.blocks == null || typeof newGraph.blocks !== "object" || Array.isArray(newGraph.blocks)) {
    errors.push(err("P3", "graph-shape",
      "an automation graph is { blocks: { <id>: block } } — 'blocks' must be an id-keyed object"));
    return errors;
  }
  const ids = Object.keys(newGraph.blocks);
  if (!ids.length) {
    errors.push(err("P4", "graph-empty", "the graph has no blocks"));
    return errors;
  }
  let hasStart = false;
  const oldBlocks = (oldGraph && oldGraph.blocks) || {};

  const scanScripts = (blockId, node, oldNode, path) => {
    if (node == null || typeof node !== "object") return;
    if (Array.isArray(node)) {
      for (let i = 0; i < node.length; i++)
        scanScripts(blockId, node[i], Array.isArray(oldNode) ? oldNode[i] : undefined, `${path}[${i}]`);
      return;
    }
    for (const key of Object.keys(node)) {
      const value = node[key];
      const oldValue = oldNode != null && typeof oldNode === "object" ? oldNode[key] : undefined;
      if (GRAPH_SCRIPT_KEYS.indexOf(key) >= 0 && typeof value === "string" && value.trim()) {
        if (value.indexOf("«code removed") >= 0) {
          errors.push(err("P2", "graph-marker",
            `block '${blockId}' echoes a code-removed marker at ${path}.${key} — omit the key; existing scripts carry over`));
        } else if (oldValue !== value) {
          errors.push(err("P2", "graph-script",
            `block '${blockId}' would author script content at ${path}.${key} — scripts inside blocks are not writable through ops (D7); only a verbatim carry of the existing value is accepted`));
        }
        continue;
      }
      scanScripts(blockId, value, oldValue, `${path}.${key}`);
    }
  };

  for (const id of ids) {
    const block = newGraph.blocks[id];
    if (block == null || typeof block !== "object" || Array.isArray(block)) {
      errors.push(err("P3", "graph-block", `block '${id}' must be an object`));
      continue;
    }
    if (typeof block.name !== "string" || !block.name) {
      errors.push(err("P3", "graph-block", `block '${id}' needs a 'name' (its type: start, sequence, ifElse, forEach, trigger, …)`));
      continue;
    }
    if (block.name === "start") hasStart = true;

    for (const side of ["inputs", "outputs"]) {
      const io = block[side];
      if (io == null || typeof io !== "object") continue;
      for (const port of Object.keys(io)) {
        const connections = io[port] && io[port].connections;
        if (!Array.isArray(connections)) continue;
        for (const c of connections) {
          const target = c && (c.node != null ? c.node : c.output);
          if (target != null && newGraph.blocks[String(target)] === undefined)
            errors.push(err("P5", "graph-wiring",
              `block '${id}' ${side} connect to '${target}', which is not a block in this graph`));
        }
      }
    }

    scanScripts(id, block.data, oldBlocks[id] && oldBlocks[id].data, "data");
  }

  if (!hasStart)
    errors.push(err("P4", "graph-start", "every automation graph needs a 'start' block"));
  return errors;
};

/* Script-bearing keys inside BLOCK DATA. Deliberately NOT the D5 stripper list:
   'condition' is excluded here because an ifElse block's data.condition is the
   converter-mediated expression surface (the {{form.*}} chip dialect the designer
   authors) — refusing it would make AI-authored branching impossible. True script
   bodies (plugin/logic/script keys) stay closed. */
const GRAPH_SCRIPT_KEYS = [
  "backendScript", "visibilityScript", "frontendScript", "queryScript",
  "beforeScript", "afterScript", "script", "preprocess",
];

/* ---- D11 modal-save diff --------------------------------------------------------------- */

/**
 * The information design modal edits a field as a local draft and saves it back WHOLE —
 * so a sub-field or option removed inside it never passes a delete handler the guard
 * could intercept. This diff finds what a pending save DROPS, so the save path can sweep
 * those units first. Compares by id (identity), like everything else in the engine.
 */
export const diffFieldSubUnits = (oldField, newField) => {
  const dropped = { subFields: [], options: [] };
  if (!oldField) return dropped;
  const keptSubs = new Set(
    ((newField && newField.fields) || []).filter(Boolean).map((s) => String(s.id)));
  for (const s of oldField.fields || [])
    if (s && s.id != null && !keptSubs.has(String(s.id))) dropped.subFields.push(s);
  const keptOptions = new Set(
    ((newField && newField.optionList) || []).filter(Boolean).map((o) => String(o.id)));
  for (const o of oldField.optionList || [])
    if (o && o.id != null && !keptOptions.has(String(o.id))) dropped.options.push(o);
  return dropped;
};

/**
 * Guard rows for a pending modal save of `newField` over `oldField` (both top-level
 * information fields): every dropped sub-field/option contributes its composeCascade rows
 * WITHOUT the target remove op (the save itself performs the drop). Empty array = nothing
 * referenced, save cleanly.
 */
export const composeFieldSaveGuard = (canvas, oldField, newField) => {
  const dropped = diffFieldSubUnits(oldField, newField);
  const rows = [];
  for (const s of dropped.subFields) {
    const composed = composeCascade(canvas, [["information", oldField.id], ["fields", s.id]]);
    if (!composed.errors) rows.push(...composed.rows);
  }
  for (const o of dropped.options) {
    const composed = composeCascade(canvas, [["information", oldField.id], ["optionList", o.id]]);
    if (!composed.errors) rows.push(...composed.rows);
  }
  return { dropped, rows };
};

/* ---- labels --------------------------------------------------------------------------- */

export const describeOp = (op) => {
  const at = op.address && op.address.length
    ? op.address.map((h) => h[0] + "[" + h[1] + "]").join(".")
    : "canvas";
  switch (op.op) {
    case "set": return (op.unset ? "Clear " : "Set ") + op.key + " on " + at;
    case "upsert": return "Write " + at;
    case "remove": return "Remove " + at;
    case "move": return "Reorder " + at;
    default: return op.op + " " + at;
  }
};

/* Human labels for checklist/dialog rows: resolved against the draft so the user reads
   "Clear the formula on field 'Amount'", never raw id chains. */
const UNIT_LABELS = {
  informationField: "field", subField: "sub-field", option: "option", state: "state",
  flow: "flow", conditionalRoute: "conditional route", activity: "activity",
  standardActivity: "form", formRow: "form row", formItem: "form element",
  itemSubfieldConfig: "column config", formTab: "tab", aiAction: "GNey action",
  menu: "listing", listColumn: "column", mobileColumn: "mobile column",
  filterItem: "filter condition", document: "document", trigger: "automation",
  action: "action button",
};

const nodeDisplay = (node) =>
  node == null ? null : (node.displayName || node.name || node.header || node.label || null);

const shortValue = (value) => {
  if (value === undefined) return "nothing";
  if (value === null) return "empty";
  if (typeof value === "string") {
    const s = value.length > 42 ? value.slice(0, 39) + "…" : value;
    return `"${s}"`;
  }
  if (typeof value === "object") return Array.isArray(value) ? "a list" : "new settings";
  return String(value);
};

/* Best-effort chain walk for labeling — never throws, tolerates malformed addresses
   (e.g. a model putting the key as a hop): returns the deepest resolvable node plus how
   many hops were actually consumed, so callers can flag leftover hops. */
const resolveForLabel = (canvas, address) => {
  let unitName = "root";
  let node = canvas;
  let resolved = null;
  let consumed = 0;
  for (const hop of address || []) {
    if (!Array.isArray(hop) || hop.length !== 2) break;
    const unit = UNIT_CATALOG[unitName];
    const childUnit = unit && unit.children && unit.children[hop[0]];
    if (!childUnit) break;
    const collection = getPath(node, hop[0]);
    if (!Array.isArray(collection)) break;
    const idKey = UNIT_CATALOG[childUnit].idKey;
    const member = collection.find((m) => m && sameId(m[idKey], hop[1]));
    if (!member) {
      resolved = { unitName: childUnit, node: null, partial: true, consumed };
      break;
    }
    unitName = childUnit;
    node = member;
    consumed += 1;
    resolved = { unitName: childUnit, node: member, partial: false, consumed };
  }
  return resolved;
};

/**
 * A cheap per-row viability check for checklist rendering: grammar + address
 * resolvability only (net-state validation stays at apply). Rows that fail here can
 * never apply, so the card greys them out with the reason instead of letting the user
 * hit Apply and read a raw refusal. Returns null when the row looks viable, else a
 * plain-language reason.
 */
export const precheckOp = (op, canvas) => {
  const grammar = grammarCheck(op);
  if (grammar.length) return grammar[0].message;
  if (op.op === "set" && op.address.length === 0) {
    return UNIT_CATALOG.root.rootScalarKeys.indexOf(op.key) >= 0
      ? null
      : `'${op.key}' is not a settable root key`;
  }
  if (!canvas) return null;
  const hit = resolveForLabel(canvas, op.address);
  if (!hit) return "this address doesn't match anything in the draft — ask GNey to re-read and regenerate";
  if (hit.partial) {
    // a legal collection, but no member with that id — creation is fine at the final hop
    if (op.op === "upsert" && hit.consumed === op.address.length - 1) return null;
    return "the element this points at is not in the draft (it may have changed) — ask GNey to re-read and regenerate";
  }
  if (hit.consumed < op.address.length) {
    const leftover = op.address[hit.consumed];
    return `'${Array.isArray(leftover) ? leftover[0] : leftover}' is not part of a canvas address here — ask GNey to regenerate this step`;
  }
  return null;
};

/**
 * A checklist-row label in the user's language, resolved against `canvas` (the draft).
 * Falls back to the technical describeOp form — suffixed honestly — when the address
 * doesn't resolve (such an op will be refused at apply anyway).
 */
export const describeOpHuman = (op, canvas) => {
  if (!op || typeof op !== "object") return "(malformed operation)";

  // root scalars
  if (op.op === "set" && Array.isArray(op.address) && op.address.length === 0) {
    const rootNames = {
      ai_instruction: "the module's AI instruction", title: "the entry-title template",
      transaction: "transaction mode", archive: "the archive configuration",
    };
    const what = rootNames[op.key] || `'${op.key}'`;
    return op.unset ? `Clear ${what}` : `Set ${what} to ${shortValue(op.value)}`;
  }

  const hit = canvas ? resolveForLabel(canvas, op.address) : null;
  const unitLabel = hit ? (UNIT_LABELS[hit.unitName] || hit.unitName) : "element";
  let name = hit && hit.node ? nodeDisplay(hit.node) : null;
  // form elements usually carry no name of their own — they render an information field;
  // resolve through to the field's display name so the row reads like the designer does
  if (!name && hit && hit.node && hit.unitName === "formItem" && hit.node.id != null) {
    const field = (canvas.information || []).find((f) => f && sameId(f.id, hit.node.id));
    if (field) name = field.displayName;
  }
  if (!name && hit && hit.node) name = String(op.address[op.address.length - 1][1]);

  if (!hit || !hit.node) {
    // unresolvable (stale id, malformed hop like ["key","formula"]) — say so plainly
    if (op.op === "upsert") {
      const payloadName = nodeDisplay(op.item);
      if (payloadName) return `Add ${unitLabel} "${payloadName}"`;
    }
    return describeOp(op) + " — couldn't match this to the draft, it will be refused";
  }

  switch (op.op) {
    case "set": {
      if (typeof op.key !== "string" || !op.key)
        return `Change ${unitLabel} "${name}" — no key given, it will be refused`;
      if (op.key === "displayName" || op.key === "name")
        return op.unset
          ? `Clear the name of ${unitLabel} "${name}"`
          : `Rename ${unitLabel} "${name}" to ${shortValue(op.value)}`;
      const keyLabel = /formula/i.test(op.key) ? `the ${op.key}` : `'${op.key}'`;
      return op.unset
        ? `Clear ${keyLabel} on ${unitLabel} "${name}"`
        : `Set ${keyLabel} on ${unitLabel} "${name}" to ${shortValue(op.value)}`;
    }
    case "upsert": {
      const payloadName = nodeDisplay(op.item) || name;
      // an automation with a graph names its size — the card's honest summary
      if (hit.unitName === "trigger" && op.item && op.item.automation && op.item.automation.blocks) {
        const n = Object.keys(op.item.automation.blocks).length;
        return `Update automation "${payloadName}" (${n} block${n === 1 ? "" : "s"})`;
      }
      return `Update ${unitLabel} "${payloadName}"`;
    }
    case "remove":
      return `Remove ${unitLabel} "${name}"`;
    case "move": {
      const anchorId = op.before != null ? op.before : op.after;
      const sibling = resolveForLabel(canvas,
        (op.address || []).slice(0, -1).concat([[op.address[op.address.length - 1][0], anchorId]]));
      const anchorName = sibling && sibling.node ? (nodeDisplay(sibling.node) || anchorId) : anchorId;
      return `Move ${unitLabel} "${name}" ${op.before != null ? "before" : "after"} "${anchorName}"`;
    }
    default:
      return describeOp(op);
  }
};

const describeProposal = (ops) =>
  ops.length === 1 ? describeOp(ops[0]) : ops.length + " canvas edits";
